import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";

import {
  savePlant,
  editPlant,
  getPlantDetails,
  getRONFElements,
} from "../../network/cipAPI";
import {
  storeUnits,
  storeCostInformation,
} from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import ErrorLabel from "../../components/ErrorLabel";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DupontButton from "../../components/DupontButton";
import PageLoading from "../../components/PageLoading";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import Settings from "../home/Settings";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import {
  getChangedData,
  updatePlantId,
  getSelectedPlantTrainInfo,
} from "../../utils/Utils";
import { saveItemSession, setLocalStorageItem } from "../../utils/session";
import { WATER_TYPE } from "./addNewPlant.config";
import RoConfiguration from "./roConfiguration";
import { API_URLS } from "../../network/appUrls";
import "./index.scss";
import CostInformation from "../costInformation";
import Footer from "../../common/footer/Footer";
import CommentsOnSpecialElements from "../../components/CommentsOnSpecialElements";

const basePath = API_URLS.basePath;

const AddNewPlant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const costInformationRef = React.useRef();

  const [noOfTrains, setNoOfTrains] = useState(location.state?.noOfTrains);
  const [waterTypeOptions, setWaterTypeOptions] = useState(WATER_TYPE(t));
  const [plantDetail, setPlantDetail] = useState([]);
  const [plantDetailerror, setPlantDetailError] = useState([]);
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [RONFElements, setRONFElements] = useState([]);
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState({
    plantInformation: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    roConfiguration: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    settings: {
      fulfilled: true, // Always fulfilled - radio buttons always have valid values
      changed: false,
      error: false,
    },
    costInformation: {
      fulfilled: false,
      changed: false,
      error: false,
    },
  });

  const {
    token,
    flow,
    pressure,
    feed_quality,
    feed_water_factor,
    permeate_quality,
    feed_water_permeate_factor,
    temperature,
    flux,
    volume,
    mass,
    currency,
    conversion_rate,
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    plantData,
    accessUserPlant,
  } = useSelector((state) => state);

  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((item) => item.label),
    [],
  );

  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("isEdit");
  const plantId = searchParams.get("plantId");

  const storeInitialConfig = (trains) => {
    if (!trains) return;
    const result = {};
    Object.values(trains).forEach((train) => {
      const trainId = train.train_id;
      result[trainId] = {};
      train.passes.forEach((pass) => {
        const passId = pass.pass_id;
        result[trainId][passId] = {};
        pass.stages.forEach((stage) => {
          const stageId = stage.stage_id;
          const numElements =
            Number(stage.num_pressure_vessels) *
            Number(stage.num_element_per_pressure_vessel);
          result[trainId][passId][stageId] = numElements;
        });
      });
    });
    setLocalStorageItem("prevTrainConfig", JSON.stringify(result));
  };

  function normalizeRoNfElementUsed(trains) {
    const newTrains = JSON.parse(JSON.stringify(trains));
    for (const train of Object.values(newTrains)) {
      for (const pass of train.passes) {
        for (const stage of pass.stages) {
          if (stage.is_isd) {
            if (!Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used
                ? [stage.ro_nf_element_used]
                : [];
            }
          } else {
            if (Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used[0] || "";
            } else if (!stage.ro_nf_element_used) {
              stage.ro_nf_element_used = "";
            }
          }
        }
      }
    }
    return newTrains;
  }

  const selectedElementsCommentsData = useMemo(() => {
    if (!trains || Object.keys(trains).length === 0 || !RONFElements?.length) {
      return [];
    }

    // Collect all unique elements from DuPont trains
    const uniqueElements = new Set();
    Object.values(trains).forEach((train) => {
      if (!train.is_dupont_element) return;
      train.passes?.forEach((pass) => {
        pass.stages?.forEach((stage) => {
          const used = stage.ro_nf_element_used;
          if (!used) return;

          if (Array.isArray(used)) {
            used.forEach((el) => el?.trim() && uniqueElements.add(el));
          } else if (typeof used === "string" && used.trim()) {
            uniqueElements.add(used);
          }
        });
      });
    });

    // Group elements by their comments
    const commentsMap = new Map();
    uniqueElements.forEach((element) => {
      const elementObj = RONFElements.find(
        (item) => item.id === element && item.comment,
      );
      if (elementObj) {
        const { comment, id } = elementObj;
        if (!commentsMap.has(comment)) {
          commentsMap.set(comment, []);
        }
        commentsMap.get(comment).push(id);
      }
    });

    return Array.from(commentsMap, ([comment, elements]) => ({
      elements,
      comment,
    }));
  }, [trains, RONFElements]);

  useEffect(() => {
    if (waterType === "") {
      return;
    }
    setRONFElements([]);
    let options = [];

    const fetchRONFElements = async () => {
      try {
        const response = await getRONFElements(waterType);
        if (response && response.data) {
          const formattedData = response.data.map((element) => {
            return {
              id: element.name,
              label: element.name,
              value: element.name,
              comment: element.comment,
            };
          });
          options = formattedData;
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("ValueStrings.somethingWentWrong"),
          "",
        );
      } finally {
        setRONFElements(options);
      }
    };

    fetchRONFElements();
  }, [waterType]);

  useEffect(() => {
    setWaterTypeOptions(WATER_TYPE(t));
  }, [t]);

  useEffect(() => {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
          const response = await getPlantDetails(plantId, false, sel_train);
          if (response && response.data) {
            const {
              name,
              description,
              city,
              country,
              num_trains,
              water_type,
              config,
              setting,
              cost,
            } = response.data;

            setFetchedPlanDetails(response.data);
            setPlantDetail({
              ...plantDetail,
              installationName: name,
              description,
              city,
            });
            setCountry(country);
            setNoOfTrains(num_trains);
            setWaterType(water_type);
            dispatch(storeUnits(setting));
            setTrains(config?.trains);
            storeInitialConfig(config?.trains);

            // Map API field names to Redux field names
            const {
              energy_price: energyPrice,
              acid_price: acidPrice,
              alkali_price: alkaliPrice,
              water_price: waterPrice,
              downtime_cost: costofDownTime,
              additional_chemical_price,
            } = cost;

            dispatch(
              storeCostInformation({
                energyPrice,
                acidPrice,
                alkaliPrice,
                waterPrice,
                costofDownTime,
                additional_chemical_price,
              }),
            );
          }
        } catch (error) {
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, country: "" });
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      plantInformation: { ...prev.plantInformation, changed: true },
    }));
  };
  const changeWaterType = (value) => {
    setWaterType(value);
    setPlantDetailError({ ...plantDetailerror, waterType: "" });
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      plantInformation: { ...prev.plantInformation, changed: true },
    }));
  };
  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    if (key === "installationName") {
      const error =
        value.trim() === "" ? t("addNewPlant.installationNameRequired") : "";
      setPlantDetailError({ ...plantDetailerror, [key]: error });
    } else {
      setPlantDetailError({ ...plantDetailerror, [key]: "" });
    }
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      plantInformation: { ...prev.plantInformation, changed: true },
    }));
  };

  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    let plantInfoValid = true;
    let roConfigValid = true;
    const error = [];
    // Validate Plant Information
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["installationName"] = t("addNewPlant.installationNameRequired");
    }
    if (!country) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["country"] = t("addNewPlant.countryRequired");
    }
    if (!waterType) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["waterType"] = t("addNewPlant.waterTypeRequired");
    }

    // Update plant information status
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      plantInformation: {
        fulfilled: plantInfoValid,
        changed: false,
        error: !plantInfoValid,
      },
    }));

    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    // Validate RO Configuration
    for (const train of Object.values(trains)) {
      for (const pass of train.passes) {
        if (pass?.stages?.length === 0) {
          validationSuccessful = false;
          roConfigValid = false;
          break;
        }
        for (const stage of pass.stages) {
          // Check for ISD: must be non-empty array
          if (stage.is_isd) {
            if (
              !Array.isArray(stage.ro_nf_element_used) ||
              stage.ro_nf_element_used.length === 0
            ) {
              validationSuccessful = false;
              roConfigValid = false;
              break;
            }
          } else {
            // Single: must be non-empty string
            if (
              !stage.ro_nf_element_used ||
              (typeof stage.ro_nf_element_used === "string" &&
                stage.ro_nf_element_used.trim() === "")
            ) {
              validationSuccessful = false;
              roConfigValid = false;
              break;
            }
          }
          if (
            !stage.num_pressure_vessels ||
            !stage.num_element_per_pressure_vessel
          ) {
            validationSuccessful = false;
            roConfigValid = false;
            break;
          }
        }
      }
    }

    // Update RO configuration status
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      roConfiguration: {
        fulfilled: roConfigValid,
        changed: false,
        error: !roConfigValid,
      },
    }));

    return validationSuccessful;
  };

  const savePlantDetails = async (key, opts = {}) => {
    if (!opts.skipLoading) setIsLoading(true);
    setIsSaving(true);
    try {
      const validated = runValidation();
      setStageValidationSucessfull(validated);
      let response = null;
      if (validated) {
        // Normalize ro_nf_element_used for all stages before building payload
        const normalizedTrains = normalizeRoNfElementUsed(trains);
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_quality: feed_quality,
            feed_water_factor: feed_water_factor,
            permeate_quality: permeate_quality,
            feed_water_permeate_factor: feed_water_permeate_factor,
            temperature: temperature,
            flux: flux,
            volume: volume,
            mass: mass,
            currency: currency,
            conversion_rate: conversion_rate,
          },
          cost: {
            energy_price: Number(energyPrice),
            acid_price: Number(acidPrice),
            alkali_price: Number(alkaliPrice),
            water_price: Number(waterPrice),
            downtime_cost: Number(costofDownTime),
            additional_chemical_price: Number(additional_chemical_price),
          },
          config: {
            trains: [...Object.values(normalizedTrains)],
          },
          tol_num_elements: totalNumberOfElements,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("plantName", plantDetail.installationName);
        if (plantId && isEdit === "true") {
          const editedFields = getChangedData(payload, fetchedPlanDetails);

          // Convert cost values to strings for API if they exist in editedFields
          if (editedFields.cost) {
            Object.keys(editedFields.cost).forEach((key) => {
              editedFields.cost[key] = String(editedFields.cost[key]);
            });
          }

          response = await editPlant(plantId, editedFields);
        } else {
          // Convert cost values to strings for API
          if (payload.cost) {
            Object.keys(payload.cost).forEach((key) => {
              payload.cost[key] = String(payload.cost[key]);
            });
          }
          response = await savePlant(payload);
        }
        if (response.created_at) {
          // Reset all changed flags after successful save
          setMandatoryComponentsStatus((prev) => ({
            plantInformation: { ...prev.plantInformation, changed: false },
            roConfiguration: { ...prev.roConfiguration, changed: false },
            settings: { ...prev.settings, changed: false },
            costInformation: { ...prev.costInformation, changed: false },
          }));

          if (key === "exit") {
            if (accessUserPlant) {
              dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
              navigate("/user-management");
            } else {
              navigate("/plants");
            }
            createNotification(
              SUCCESS_NOTIFICATION,
              t("addNewPlant.successfullySaved"),
              "",
            );
          } else if (!opts.skipNavigate) {
            updatePlantId(response.plant_id);
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${response.plant_id}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${response.plant_id}`;
            navigate(path);
          }
        } else {
          createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        }
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      if (!opts.skipLoading) setIsLoading(false);
      setIsSaving(false);
    }
  };

  if (typeof window !== "undefined") {
    window.savePlantDetails = savePlantDetails;
  }

  const hasUnsavedChanges = useMemo(() => {
    // Don't block navigation during save operations
    if (isSaving) return false;

    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus, isSaving]);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  return (
    <div className="container-main">
      <Header />

      <div className="main-container">
        <div className="content-container descriptionText">
          {isLoading ? (
            <PageLoading customHeight={"515px"} />
          ) : (
            <div>
              <div className="calculatedValueText">
                {t("roAdvisorProject.plantsetup")}
              </div>
              <div className="ro-card-wrapper position-relative">
                <div className="ro-card-header">
                  <div className="teal-heading ">
                    {t("plantInformationStrings.plantInfo")}
                  </div>
                </div>
                <div className="grid-container-row">
                  <InputBox
                    name={"installationName"}
                    value={plantDetail["installationName"]}
                    labelText={t(`addNewPlant.${"installationName"}`)}
                    placeholder={t(`addNewPlant.${"installationName"}`)}
                    rows={1}
                    isRequired={true}
                    onChange={(value) =>
                      handleInputChange("installationName", value)
                    }
                    errorMsg={plantDetailerror["installationName"]}
                    maxLength={250}
                  />
                  <InputBox
                    name={"city"}
                    value={plantDetail["city"]}
                    labelText={t(`addNewPlant.${"city"}`)}
                    placeholder={t(`addNewPlant.${"city"}`)}
                    rows={1}
                    isRequired={false}
                    onChange={(value) => handleInputChange("city", value)}
                    errorMsg={plantDetailerror["city"]}
                    maxLength={100}
                  />
                </div>
                <div className="grid-container-row marginTop5 marginBtm5">
                  <Dropdown
                    labelText={t("addNewPlant.country")}
                    placeholder={t("addNewPlant.country")}
                    value={country}
                    onChange={changeCountry}
                    options={countryOptions}
                    errorMsg={plantDetailerror["country"]}
                    isRequired
                    showCancelIcon
                  />
                  <Dropdown
                    labelText={t("addNewPlant.waterType")}
                    placeholder={t("addNewPlant.waterType")}
                    value={waterType}
                    onChange={changeWaterType}
                    options={waterTypeOptions}
                    errorMsg={plantDetailerror["waterType"]}
                    isRequired
                    showCancelIcon
                  />
                </div>

                <InputBox
                  name={"description"}
                  value={plantDetail["description"]}
                  labelText={t(`addNewPlant.${"description"}`)}
                  placeholder={t(`addNewPlant.${"description"}`)}
                  rows={3}
                  isRequired={false}
                  onChange={(value) => handleInputChange("description", value)}
                  errorMsg={plantDetailerror["description"]}
                  maxLength={500}
                />
              </div>
              <div>
                <div className="ro-card-wrapper position-relative">
                  <div className="ro-card-header">
                    <div className="info-section">
                      <div className="teal-heading">
                        {t("addNewPlant.reverseOsmosis")}
                      </div>
                      <IconWithTooltip
                        label={t("helpInformation.roConfigurationHelp")}
                      />

                      {!stageValidationSucessfull && (
                        <ErrorLabel
                          className="red-heading margin-left-ro"
                          message={t("addNewPlant.mandatoryRequired")}
                        />
                      )}
                    </div>
                  </div>

                  <div className="description-text ">
                    <RoConfiguration
                      trains={trains}
                      setTrains={setTrains}
                      setTotalNumberOfElements={setTotalNumberOfElements}
                      stageValidationSucessfull={stageValidationSucessfull}
                      setStageValidationSucessfull={
                        setStageValidationSucessfull
                      }
                      noOfTrains={noOfTrains}
                      RONFElements={RONFElements}
                      setMandatoryComponentsStatus={
                        setMandatoryComponentsStatus
                      }
                    />

                    <div className="flex-jc-end padding-top-20 align-items-center ">
                      {t("addNewPlant.totalNoOfElements")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfElements}
                          placeholder={t(`addNewPlant.totalNoOfElements`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                  </div>
                  <CommentsOnSpecialElements
                    commentsData={selectedElementsCommentsData}
                  />
                </div>
                <Settings
                  disabled={false}
                  setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                />
                <CostInformation
                  ref={costInformationRef}
                  setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                />
                <div className=" button-right-container padding-top-20 ">
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("exit")}
                    disabled={costInformationRef.current?.hasErrors()}
                  >
                    {t("ValueStrings.saveExit")}
                  </DupontButton>
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("continue")}
                    disabled={costInformationRef.current?.hasErrors()}
                  >
                    {t("ValueStrings.saveContinue")}
                  </DupontButton>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default AddNewPlant;
